/* eslint no-console: 0 */

import axios from 'axios';
import { getHotelId } from './util';
import { isEmpty, isNil, isString } from 'lodash';
import { getToken, setToken } from './token';
import { updateOfflineStatus } from './offline';
import { omitDeep } from './omitDeep';
import Vue from 'vue';
import { baseApiAddresses } from './urls';
import Protobuf from '@/plugins/proto';
import Redactyl from 'redactyl.js';
import ServiceUrls from '@/lib/ServiceUrls';

const baseApiAddress = baseApiAddresses[process.env.HOTELMEISTER_ENV];
axios.defaults.baseURL = baseApiAddress;

axios.interceptors.request.use(
  (config) => {
    if (config.url.indexOf('http') >= 0 && config.url.indexOf(baseApiAddress) < 0 && config.url.indexOf('https://report.hotelmeister.io/') < 0) {
      // do nothing for now
    } else if (config.baseURL === baseApiAddress && !config.headers.Authorization) {
      let hotelId = getHotelId(config.url);
      if (isNil(hotelId)) {
        hotelId = config.hotelId;
      }
      hotelId = isNil(hotelId) ? config.headers['x-hotelsoftware-hotelid'] : hotelId;

      if (isEmpty(hotelId)) {
        hotelId = window.location.href.replace(/^[^#]+\/#\/([0-9a-f]+).*$/i, '$1');
      }

      if (isEmpty(hotelId)) {
        hotelId = window.HmVue.instances.MyApp.hotel;
      }

      const token = getToken(hotelId);

      if (!isEmpty(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        let url = config.url;

        if (!isEmpty(url) && isString(url)) {
          if (url.indexOf(config.baseURL) === 0) {
            url = url.substring(config.baseURL.length - 1);
          }

          switch (url) {
            case ServiceUrls.login():
            case ServiceUrls.resetPassword():
            case ServiceUrls.logout():
            case ServiceUrls.log.info:
            case ServiceUrls.log.warn:
            case ServiceUrls.log.error:
              break;
            default:
              if (url.indexOf('/report/') < 0 && url.indexOf('/ui/version') < 0) {
                return Promise.reject(new Error('AUTH TOKEN INVALID OR MISSING'));
              }
              break;
          }
        }
      }

      config.headers['x-hotelsoftware-referer'] = window.location.href;
      config.headers['x-hotelsoftware-version'] = process.env.VERSION;
      config.headers['x-hotelsoftware-language'] = window.i18n.locale;
      config.headers['x-hotelsoftware-tabid'] = sessionStorage.getItem('tabId');
      config.headers['Accept-Language'] = window.i18n.locale || 'de';
    }

    if (isNil(config.metadata)) {
      config.metadata = {};
    }

    config.metadata.startTime = new Date();

    return config;
  },
  (err) => {
    if (!err.config) err.config = {};
    const config = err.config;

    if (err.status === 401) {
      return Promise.reject(err);
    }

    // if error status is not 500 --> return because we do not retry any client errors
    if (err.status !== 500) {
      updateOfflineStatus();
      return Promise.reject(err);
    }

    // Set the variable for keeping track of the retry count
    config.retryCount = config.retryCount || 0;

    // Check if we've maxed out the total number of retries
    if (config.retryCount >= 3) {
      // Reject with the error
      updateOfflineStatus();
      return Promise.reject(err);
    }

    // Increase the retry count
    config.retryCount += 1;

    // Create new promise to handle exponential backoff
    const backoff = new Promise(((resolve) => {
      setTimeout(() => {
        resolve();
      }, 300 * config.retryCount + 150);
    }));

    // Return the promise in which recalls axios to retry the request
    return backoff.then(() => axios(config));
  },
);

const redactly = new Redactyl({
  properties: [
    'pw',
    'pass',
    'passwd',
    'password',
    'password_confirmation',
    'passwordConfirmation',
    'confirm_password',
    'confirmPassword',
    'secret',
    'secret_token',
    'secretToken',
    'secret_key',
    'secretKey',
    'api_key',
    'access_token',
    'accessToken',
    'authenticity_token',
    'oauth_token',
    'token',
    'user_session_secret',
    'request.session.csrf',
    'request.session._csrf',
    'request.params._csrf',
    'request.cookie',
    'request.cookies'],
});

const axiosErrorHandler = (error) => {
  updateOfflineStatus();

  const response = error.response;

  if (!isNil(response) && !isNil(response.headers) && response.headers['x-hotelsoftware-tokenexpired'] === '1') {
    if (!isNil(axios.$serviceBus)) axios.$serviceBus.$emit('logout');
  }

  if (!isNil(response) && !response.config.url.includes('/echo/keepAlive')) {
    try {
      if (!isNil(response.config.metadata)) {
        response.config.metadata.endTime = new Date();
        response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
      }

      let request = null;
      try {
        if (!isNil(error.config) && !isEmpty(error.config.data)) {
          // request = JSON.parse(error.config.data);
          if (typeof request === 'string') request = JSON.parse(request);
          // request = omitDeep(request, Vue.rollbar.options.scrubFields);
          request = redactly.redact(request);
        }
      } catch (ignore) {
        request = error.config.data;
      }

      if (parseInt(response.status, 10) !== 200) {
        Vue.rollbar.captureEvent('manual', {
          category: 'Network',
          action: `Received response with status ${response.status} for ${response.config.url}`,
          metadata: {
            method: response.config.method.toUpperCase(),
            requestUrl: response.config.url,
            status: response.status,
            duration: response.duration,
            url: window.location.href,
            hotel: response.headers['x-hotelsoftware-hotelid'],
            user: response.headers['x-hotelsoftware-username'],
            request: request,
          },
        }, 'info');

        if (response.status !== 401 && response.status !== 422) {
          Vue.rollbar.error(`Received response with status ${response.status} for ${response.config.url}`);
        }
      }
      // eslint-disable-next-line no-empty
    } catch (ignore) {
      console.error(ignore);
    }
  } else if (isNil(response) && !isNil(error.config)) {
    try {
      if (!isNil(error.config.metadata)) {
        error.config.metadata.endTime = new Date();
        error.duration = error.config.metadata.endTime - error.config.metadata.startTime;
      }

      let request = null;
      try {
        if (!isNil(error.config) && !isEmpty(error.config.data)) {
          // request = JSON.parse(error.config.data);
          if (typeof request === 'string') request = JSON.parse(request);
          // request = omitDeep(request, Vue.rollbar.options.scrubFields);
          request = redactly.redact(request);
        }
      } catch (ignore) {
        request = error.config.data;
      }

      Vue.rollbar.captureEvent('manual', {
        category: 'Network',
        action: `Received response with status UNKNOWN for ${error.config.url}`,
        metadata: {
          method: error.config.method.toUpperCase(),
          requestUrl: error.config.url,
          status: 'UNKNOWN',
          duration: error.duration,
          url: window.location.href,
          request: request,
        },
      }, 'info');

      Vue.rollbar.error(`Received response with status UNKNOWN for ${error.config.url}`);
      // eslint-disable-next-line no-empty
    } catch (ignore) {
      console.error(ignore);
    }
  }

  return Promise.reject(error);
};

axios.interceptors.response.use(
  (response) => {
    if (!isEmpty(response.data.errors) || response.data.status >= 400) {
      if (!isNil(response.config.metadata)) {
        response.config.metadata.endTime = new Date();
        response.config.metadata.duration = `${response.config.metadata.endTime - response.config.metadata.startTime}ms`;
        response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
      }

      try {
        let request = response.config.data;
        try {
          if (typeof request === 'string') request = JSON.parse(request);
          request = redactly.redact(request);
        } catch (ignore) {
          request = response.config.data;
        }

        Vue.rollbar.captureEvent('manual', {
          category: 'Network',
          action: `Received response with status ${response.data.status} for ${response.config.url}`,
          metadata: {
            url: window.location.href,
            request: {
              method: response.config.method.toUpperCase(),
              requestUrl: response.config.url,
              status: response.data.status,
              duration: response.duration,
              metadata: response.config.metadata,
              headers: response.config.headers,
              request: request,
            },
            response: {
              headers: response.headers,
              response: response.data,
            },
          },
        }, 'error');

        // eslint-disable-next-line no-empty
      } catch (e) {

      }

      const customError = new Error('API Failure');
      response.original_status = response.status;
      response.status = response.data.status;
      customError.response = response;
      return axiosErrorHandler(customError);
    }

    // has to be lower case because all headers are lower case in axios (not sure why?)
    let hotelId = getHotelId(response.config.url);
    hotelId = isNil(hotelId) ? response.headers['x-hotelsoftware-hotelid'] : hotelId;
    const newToken = response.headers['x-hotelsoftware-token'];
    if (!isEmpty(newToken)) {
      setToken(hotelId, newToken);
    }

    if ((!isNil(response) && !isNil(response.config) && !isNil(response.config.url) && !response.config.url.includes('/echo/keepAlive')) || response.status !== 200) {
      try {
        if (!isNil(response.config.metadata)) {
          response.config.metadata.endTime = new Date();
          response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
        }

        let request = null;
        try {
          if (!isNil(response.config) && !isEmpty(response.config.data)) {
            request = JSON.parse(response.config.data);
            if (typeof request === 'string') request = JSON.parse(request);
            // request = omitDeep(request, Vue.rollbar.options.scrubFields);
            request = redactly.redact(request);
          }
        } catch (ignore) {
          request = response.config.data;
        }

        if (parseInt(response.status, 10) !== 200) {
          Vue.rollbar.captureEvent('manual', {
            category: 'Network',
            action: `Received response with status ${response.status} for ${response.config.url}`,
            scrubFields: ['x-hotelsoftware-token', 'Authorization'],
            metadata: {
              method: response.config.method.toUpperCase(),
              requestUrl: response.config.url,
              status: parseInt(response.status, 10),
              duration: parseInt(response.duration, 10),
              url: window.location.href,
              hotel: response.headers['x-hotelsoftware-hotelid'],
              user: response.headers['x-hotelsoftware-username'],
              request: request,
            },
          }, 'info');

          if (response.status !== 401 && response.status !== 422) {
            Vue.rollbar.error(`Received response with status ${response.status} for ${response.config.url}`);
          }
        }
        // eslint-disable-next-line no-empty
      } catch (ignore) {
        console.error(ignore);
      }
    }

    return !isEmpty(response.headers['content-type']) && response.headers['content-type'].indexOf('application/x-protobuf') >= 0
      ? Protobuf.transform(response)
      : response;
  },
  axiosErrorHandler,
);

export default axios;



// WEBPACK FOOTER //
// ./src/lib/util/axios.js